import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { hrmsGetRequest, hrmsPostRequest, hrmsUpdateRequest, hrmsDeleteRequest } from "../services/index";

const API_URL = "/api/AdminObservation";

// Create admin observation - POST /add
export const createAdminObservation = createAsyncThunk(
  "adminObservation/create",
  async (observationData, { rejectWithValue }) => {
    try {
      const response = await hrmsPostRequest(`${API_URL}/add`, observationData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get admin observations by teacher email - GET /teacher/:email
export const fetchAdminObservationsByTeacher = createAsyncThunk(
  "adminObservation/fetchByTeacher",
  async (teacherEmail, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(`${API_URL}/teacher/${teacherEmail}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get admin observation by ID - GET /:id
export const fetchAdminObservationById = createAsyncThunk(
  "adminObservation/fetchById",
  async (observationId, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(`${API_URL}/${observationId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get all admin observations - GET /
export const fetchAllAdminObservations = createAsyncThunk(
  "adminObservation/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(API_URL);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Update admin observation - PUT /:id
export const updateAdminObservation = createAsyncThunk(
  "adminObservation/update",
  async ({ observationId, updateData }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(`${API_URL}/${observationId}`, updateData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Delete admin observation - DELETE /:id
export const deleteAdminObservation = createAsyncThunk(
  "adminObservation/delete",
  async (observationId, { rejectWithValue }) => {
    try {
      await hrmsDeleteRequest(`${API_URL}/${observationId}`);
      return observationId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const adminObservationSlice = createSlice({
  name: "adminObservation",
  initialState: {
    observations: [],
    currentObservation: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentObservation: (state) => {
      state.currentObservation = null;
    },
    resetObservations: (state) => {
      state.observations = [];
      state.currentObservation = null;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Create admin observation
    builder
      .addCase(createAdminObservation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAdminObservation.fulfilled, (state, action) => {
        state.loading = false;
        state.observations.unshift(action.payload); // Add to beginning of array
      })
      .addCase(createAdminObservation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch admin observations by teacher
    builder
      .addCase(fetchAdminObservationsByTeacher.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminObservationsByTeacher.fulfilled, (state, action) => {
        state.loading = false;
        state.observations = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchAdminObservationsByTeacher.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch admin observation by ID
    builder
      .addCase(fetchAdminObservationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminObservationById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentObservation = action.payload;
        // Update in observations array if it exists
        const index = state.observations.findIndex((obs) => obs.id === action.payload.id);
        if (index !== -1) {
          state.observations[index] = action.payload;
        } else {
          state.observations.push(action.payload);
        }
      })
      .addCase(fetchAdminObservationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch all admin observations
    builder
      .addCase(fetchAllAdminObservations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllAdminObservations.fulfilled, (state, action) => {
        state.loading = false;
        state.observations = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchAllAdminObservations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update admin observation
    builder
      .addCase(updateAdminObservation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdminObservation.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.observations.findIndex((obs) => obs.id === action.payload.id);
        if (index !== -1) {
          state.observations[index] = action.payload;
        }
        if (state.currentObservation?.id === action.payload.id) {
          state.currentObservation = action.payload;
        }
      })
      .addCase(updateAdminObservation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete admin observation
    builder
      .addCase(deleteAdminObservation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAdminObservation.fulfilled, (state, action) => {
        state.loading = false;
        state.observations = state.observations.filter((obs) => obs.id !== action.payload);
        if (state.currentObservation?.id === action.payload) {
          state.currentObservation = null;
        }
      })
      .addCase(deleteAdminObservation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearCurrentObservation, resetObservations } = adminObservationSlice.actions;
export default adminObservationSlice.reducer;

